import React, { useState, useEffect } from 'react';
import { Plus, Trash2, AlertTriangle, Scissors, Eraser } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

export default function FiltersTab() {
  const [blacklist, setBlacklist] = useState([]);
  const [cutFrom, setCutFrom] = useState([]);
  const [deletePhrase, setDeletePhrase] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [newBlacklist, setNewBlacklist] = useState('');
  const [newCutFrom, setNewCutFrom] = useState('');
  const [newDeletePhrase, setNewDeletePhrase] = useState('');

  useEffect(() => {
    loadFilters();
  }, []);

  const loadFilters = async () => {
    try {
      const [bl, cf, dp] = await Promise.all([
        api.get('/filters/blacklist'),
        api.get('/filters/cut-from'),
        api.get('/filters/delete-phrase')
      ]);
      
      setBlacklist(bl.data);
      setCutFrom(cf.data);
      setDeletePhrase(dp.data);
    } catch (error) {
      toast.error('שגיאה בטעינת סינונים');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlacklist = async (e) => {
    e.preventDefault();
    if (!newBlacklist.trim()) return;
    
    try {
      await api.post('/filters/blacklist', { keyword: newBlacklist });
      toast.success('מילה נוספה ל-Blacklist');
      setNewBlacklist('');
      loadFilters();
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה בהוספה');
    }
  };

  const handleAddCutFrom = async (e) => {
    e.preventDefault();
    if (!newCutFrom.trim()) return;
    
    try {
      await api.post('/filters/cut-from', { phrase: newCutFrom });
      toast.success('משפט נוסף ל-Cut From');
      setNewCutFrom('');
      loadFilters();
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה בהוספה');
    }
  };

  const handleAddDeletePhrase = async (e) => {
    e.preventDefault();
    if (!newDeletePhrase.trim()) return;
    
    try {
      await api.post('/filters/delete-phrase', { phrase: newDeletePhrase });
      toast.success('ביטוי נוסף ל-Delete Phrase');
      setNewDeletePhrase('');
      loadFilters();
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה בהוספה');
    }
  };

  const handleDeleteBlacklist = async (id) => {
    try {
      await api.delete(`/filters/blacklist/${id}`);
      toast.success('מילה נמחקה');
      loadFilters();
    } catch (error) {
      toast.error('שגיאה במחיקה');
    }
  };

  const handleDeleteCutFrom = async (id) => {
    try {
      await api.delete(`/filters/cut-from/${id}`);
      toast.success('משפט נמחק');
      loadFilters();
    } catch (error) {
      toast.error('שגיאה במחיקה');
    }
  };

  const handleDeletePhrase = async (id) => {
    try {
      await api.delete(`/filters/delete-phrase/${id}`);
      toast.success('ביטוי נמחק');
      loadFilters();
    } catch (error) {
      toast.error('שגיאה במחיקה');
    }
  };

  if (loading) {
    return <div className="text-center py-12">טוען סינונים...</div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">סינונים</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Blacklist */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-t-4 border-red-500">
          <div className="flex items-center mb-4">
            <AlertTriangle className="w-6 h-6 text-red-500 ml-2" />
            <h3 className="text-lg font-semibold text-gray-900">Blacklist</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            מילים שאם מופיעות - ההודעה נדחית לחלוטין
          </p>
          
          <form onSubmit={handleAddBlacklist} className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newBlacklist}
                onChange={(e) => setNewBlacklist(e.target.value)}
                placeholder="הוסף מילה..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </form>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {blacklist.map((item) => (
              <div key={item.id} className="flex justify-between items-center p-2 bg-red-50 rounded">
                <span className="text-sm text-gray-800">{item.keyword}</span>
                <button
                  onClick={() => handleDeleteBlacklist(item.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {blacklist.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">אין מילים</p>
            )}
          </div>
        </div>

        {/* Cut From */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-t-4 border-yellow-500">
          <div className="flex items-center mb-4">
            <Scissors className="w-6 h-6 text-yellow-500 ml-2" />
            <h3 className="text-lg font-semibold text-gray-900">Cut From</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            משפטים שממנו מוחקים עד סוף ההודעה
          </p>
          
          <form onSubmit={handleAddCutFrom} className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newCutFrom}
                onChange={(e) => setNewCutFrom(e.target.value)}
                placeholder="הוסף משפט..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-sm"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </form>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {cutFrom.map((item) => (
              <div key={item.id} className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                <span className="text-sm text-gray-800">{item.phrase}</span>
                <button
                  onClick={() => handleDeleteCutFrom(item.id)}
                  className="text-yellow-600 hover:text-yellow-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {cutFrom.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">אין משפטים</p>
            )}
          </div>
        </div>

        {/* Delete Phrase */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-t-4 border-blue-500">
          <div className="flex items-center mb-4">
            <Eraser className="w-6 h-6 text-blue-500 ml-2" />
            <h3 className="text-lg font-semibold text-gray-900">Delete Phrase</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            ביטויים שנמחקים, שאר ההודעה נשארת
          </p>
          
          <form onSubmit={handleAddDeletePhrase} className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newDeletePhrase}
                onChange={(e) => setNewDeletePhrase(e.target.value)}
                placeholder="הוסף ביטוי..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </form>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {deletePhrase.map((item) => (
              <div key={item.id} className="flex justify-between items-center p-2 bg-blue-50 rounded">
                <span className="text-sm text-gray-800">{item.phrase}</span>
                <button
                  onClick={() => handleDeletePhrase(item.id)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {deletePhrase.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">אין ביטויים</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
